import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import * as utils from '@/utils';
import { Fieldset } from './Fieldset';
import { Option } from './Option';
import { Divider } from './OptionDivider';
import * as shared from './shared';
/**
 * Get the ref name of the input in a option indexed at index.
 *
 * @param index The index of the option.
 * @returns The ref name.
 */
function getInputRefName(index) {
    return `input-${index}`;
}
/**
 * Factory function to create a choice component like RadioButtons and Checkboxes.
 *
 * @param options See {@link CreateChoiceComponentOptions}.
 * @returns A Vue component.
 */
export function createChoiceComponent({ name, isEqual, renderOptionInput, }) {
    return tsx.componentFactoryOf().create({
        name,
        props: {
            ...shared.commonProps,
            /** The value prop, determine the checked state of this group of inputs. */
            value: p.ofType().required,
            /** Options available in the component. */
            options: p.ofRoArray().default(() => []),
        },
        /** @returns The internal state. */
        data() {
            return {
                internalName: this.name ?? `${name}-name-${utils.getUniqueId()}`,
            };
        },
        computed: {
            /** @returns True if every option is hidden. */
            isEveryOptionHidden() {
                return this.options.filter(o => o.tag === 'option').every(option => option.hidden);
            },
        },
        methods: {
            /**
             * Method to render an Option component instance.
             *
             * @param optionData The option data.
             * @param index The index.
             * @returns The Option component instance.
             */
            renderOption(optionData, index) {
                const { value, hidden } = optionData;
                if (hidden === true) {
                    return utils.emptyVNode();
                }
                const checked = isEqual(this.value, value);
                return (<Option optionData={optionData} class={this.optionClasses} variant={this.variant} checked={checked} fieldsetDisabled={this.disabled} fieldsetReadonly={this.readonly} labelClass={this.optionLabelClass} scopedSlots={{
                        inputs: data => renderOptionInput.call(this, {
                            checked,
                            currentValue: this.value,
                            descriptionId: optionData.description != null
                                ? data.descriptionId
                                : undefined,
                            emitInputEvent: this.emitInputEvent,
                            name: this.internalName,
                            optionData,
                            ref: getInputRefName(index),
                        }),
                    }}/>);
            },
            /**
             * Method to call to emit new value and sync input element state.
             *
             * @param newValue The new value to emit event with.
             */
            emitInputEvent(newValue) {
                tsx.emitOn(this, 'onInput', newValue);
                /*
                Sync the input's actual check state.

                Vue does not proactively re-render a component when the <input> within's value or checked state changed.

                In other words, users can change the content of a text input, even when you set the value to a constant value.
                Same goes for checkboxes, even if you set the checked as true, user can still uncheck it.

                We usually don't notice this because we always add a onInput or onChange handler to the <input> and updates the
                Vue component's state, which most of the time result in keeping the component state and <input> state in sync.

                This method prevents the choice components from becoming out-of-sync with the given props.

                The following example demonstrate the same phenomemon in Vue 3 on Vue SFC Playground: https://play.vuejs.org/#eNq1VW1v0zAQ/isnCymd1KZA9ynqKmDaB/gAiE18soTS5JJ5c2zLdkJL1f/OxUnaaqIF9iJVqu+5y/m5lzzZsPfGxE2NLGFzl1lhPDj0tVlwJSqjrYcNWCxgC4XVFUQUGnHFVaaV85DdYnaPOVy0MaMilQ7PBl/lyh6PnK4QPK58RF6uilplXmgFWl12CUZnsOEKQBQw6nPGTSpr7HEYbupQyhvual1bQDodC/O27qK4ot982pVIxZHhsTIy9UgWwDwXDSTOryVecLaBXDhyrhOISivyaAxlash4+9qsyDBpngtV7oFCK38tfiEhb86Nj+hCFvJSZpkuUfYGmUKZ2oNfm/aiwHepV5xB0nMfUMwJfBeiCdq1isDpPpkzqVrc2DS7h+9UsPNUzxhqk9O/C1CmaYwKFc21dVLP+6QxUDvax3ua04HnM9D+K8Ml5co1UVTaw23aYE8KpHAeFdqXZLdr6itsqDGxT22JPt4v838UUmibIXTXP2zw6Qpo4RaTyWQ+bQ+n62omlc5REmN6qZ5vAR7R4SS8WP9O5GXm/IDFkXkOGhCCnjTIYxzbcze9+fRATdiYeUciWIgyvnNakbYGeaIdpFkIifaLafXPcZYMwsVZKqX++SlgrWqNBzws4x/wO0cLntDhq0WHtkHOdr6uA5376vozKe+Bk3aplhR9wvkNnZZ1y7EL+1CrnGgfxAW2H8MXgoTwxl2taJxuKGonuyGeM/pqXJ4ofU93Fp8Pck1d/NGgbXNSA8kRz2Zs+xvohC4k
                */
                this.options.forEach((option, index) => {
                    if (option.tag === 'divider') {
                        return;
                    }
                    Maybe.fromNullable(utils.getRefAsElement(this.$refs[getInputRefName(index)]))
                        .filter(el => el instanceof HTMLInputElement)
                        .ifJust($input => {
                        $input.checked = isEqual(this.value, option.value);
                    });
                });
            },
        },
        /** @returns The nodes. */
        render() {
            if (this.isEveryOptionHidden) {
                return utils.emptyVNode();
            }
            return (<Fieldset legend={this.legend} disabled={this.disabled} variant={this.variant} description={this.description} legendClasses={this.legendClasses} optionsWrapperClasses={this.optionsWrapperClasses}>
                    {this.options.map((data, index) => expr.match(data, 'tag', {
                    divider: () => <Divider />,
                    option: optionData => this.renderOption(optionData, index),
                }))}
                </Fieldset>);
        },
    });
}
